// Modules
import React, { useState, useEffect } from 'react';

// Utils
import Env from 'Env';
import {
  createJwtToken,
  deleteCreditCard,
  getCreditCard,
  paymentSuccess,
} from 'utils/api/payfabricApi';
import { upsertCustomerWithNewCreditCard } from 'utils/api/customerApi';

// Google Analytics
import {
  invoiceDetailsLoadGAEvent,
  invoiceDetailsClickGAEvent,
  paymentMethodButtonsLoadGAEvent,
  makePaymentButtonClickGAEvent,
} from './googleAnalyticsHandlers';

// Components
import {
  InvoiceDetails,
  PaymentMethodButtons,
  MakePaymentButton,
  SpreedlyExpressForm,
  SplititHostedForm,
  PayFabricPaymentsSDK,
  paymentTypes,
} from '@vitusvet/react-library';
import {
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
} from 'react-bootstrap';
import ModalHeader from 'react-bootstrap/ModalHeader';
import Loading from 'components/Common/Loading';

// Constants
import Environments from 'constants/Environments';

// CSS
import styles from './PaymentLinkMainContent.module.scss';
import PaymentRectangle from '../PaymentRectangle/PaymentRectangle';
import {
  makePayment,
  makeWalletPayment,
  saveCardRectangle,
} from 'utils/api/rectangleApi';
import { TokenPayProvider } from 'utils/store/TokenPayContext';

function PaymentLinkMainContent(props) {
  const {
    total,
    transaction,
    practice,
    formType,
    display,
    dispatchDisplay,
    paymentType,
    displayTypes,
    paymentButtons,
    handlePaymentButtonClick,
    handleSpreedlyTokenSuccess,
    handleSpreedlyScriptLoadError,
    handleClosePaymentForm,
    handlePayFabricSuccess,
    handlePayFabricError,
    handleRectangleResponse,
    redirectUrls,
    splititInitUrl = '',
    setCardType,
    setLastFourDigits,
    queryParams,
    handlePayBySavedCard,
  } = props;
  const { showForm } = display;

  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOverwriteSavedCardModal, setShowOverwriteSavedCardModal] =
    useState(false);

  const showMainContent =
    Object.values(display).every((val) => !val) || showForm;
  const transactionId = transaction?.charge?.id;
  const hasPayFabricId = /^pf_/.test(transactionId);
  const hasSpreedlyId = /^sr_/.test(transactionId);
  const hasRectangleId = /^rt_/.test(transactionId);

  const handleCancelPayFabricForm = () => {
    handleClosePaymentForm();
    setIsLoading(false);
  };

  const handlePayFabricTransactionCompleted = async (msg) => {
    setIsLoading(true);
    try {
      await paymentSuccess(transactionId, msg);

      if (transaction?.petData?.owner?.customerId) {
        const { practiceId } = transaction;
        const { customerId, paymentMethodId } = transaction?.petData?.owner;
        const { creditCardsInWallet } = await getCreditCard(
          customerId,
          practiceId,
          false
        );
        const isOldCustomerWithNewCreditCard = !!(
          paymentMethodId && creditCardsInWallet > 1
        );
        const isNewCustomer = !paymentMethodId && creditCardsInWallet;

        if (isOldCustomerWithNewCreditCard) {
          setShowOverwriteSavedCardModal(true);
        } else if (isNewCustomer) {
          await overwriteSavedCard();
        } else {
          handlePayFabricSuccess();
        }
      } else {
        handlePayFabricSuccess();
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      handlePayFabricError(error.message ?? error);
    }
  };

  const handlePayFabricTransactionFailed = async (msg) => {
    setIsLoading(true);
    handlePayFabricError(msg.errorMessage);
    setIsLoading(false);
  };

  const handleMakePayment = async () => {
    makePaymentButtonClickGAEvent(paymentType);

    paymentType === paymentTypes?.savedCard
      ? await handlePayBySavedCard()
      : dispatchDisplay({ type: displayTypes?.showForm });
  };

  const handleRectangleTokenResponse = async (token, isSaveCardSelected) => {
    let response = {};
    const { customerId, paymentMethodId } = transaction?.petData?.owner;

    if (isSaveCardSelected) {
      const newCustomerId = saveCardRectangle(
        token,
        transaction.petData?.name,
        customerId,
        paymentMethodId,
        transaction.practiceId
      );
      const reqBody = {
        walletId: newCustomerId,
        amount: Number(transaction.charge?.amount),
        transactionId: transactionId,
        invoiceNumber: transaction.invoiceId,
        petOwnerName: transaction.petData?.name,
      };

      response = await makeWalletPayment(reqBody);
    } else {
      const reqBody = {
        token,
        invoiceNumber: transaction.invoiceId,
        petOwnerName: transaction.petData?.name,
        amount: Number(transaction.charge?.amount),
        transactionId: transactionId,
      };

      response = await makePayment(reqBody);
    }

    handleRectangleResponse(response);
  };

  const overwriteSavedCard = async () => {
    const { practiceId } = transaction;
    const {
      phone: petOwnerPhone,
      email: petOwnerEmail,
      customerId,
      paymentMethodId,
    } = transaction?.petData?.owner;

    setIsLoading(true);
    await upsertCustomerWithNewCreditCard(
      paymentMethodId && { payment_methods: [{ id: paymentMethodId }] },
      customerId,
      practiceId,
      petOwnerPhone,
      petOwnerEmail,
      { setCardType, setLastFourDigits, queryParams }
    );

    await closeOverwriteModal(false);
  };

  const closeOverwriteModal = async (deleteNewestCreditCard) => {
    const { practiceId } = transaction;
    const { customerId } = transaction?.petData?.owner;

    if (deleteNewestCreditCard) {
      setIsLoading(true);
      await deleteCreditCard(customerId, practiceId, false);
      setIsLoading(false);
    }

    setShowOverwriteSavedCardModal(false);
    handlePayFabricSuccess();
  };

  useEffect(
    () => {
      if (!hasPayFabricId || paymentType !== paymentTypes.full || !showForm)
        return;

      setIsLoading(true);
      getToken();

      async function getToken() {
        const jwtToken = await createJwtToken(transactionId);
        setToken(jwtToken);
      }
    },
    // We need showForm in the dependency array to get a new token
    // if the user clicks the cancel button and then tried to come back
    // Otherwise PayFabric will display an error
    // eslint-disable-next-line
    [hasPayFabricId, transactionId, showForm]
  );

  return (
    <TokenPayProvider enableTokenPay={hasRectangleId}>
      {showMainContent && (
        <InvoiceDetails
          total={total}
          idObj={{ label: 'Invoice #', value: transaction?.invoiceId }}
          lineItems={[
            {
              name: transaction?.charge?.serviceType,
              quantity: '1',
              price: total,
            },
          ]}
          gaEventHandlers={{
            click: invoiceDetailsClickGAEvent,
            load: invoiceDetailsLoadGAEvent,
          }}
          startExpanded={paymentButtons.length > 1 ? false : true}
        />
      )}

      {showMainContent && (
        <PaymentMethodButtons
          clickHandler={handlePaymentButtonClick}
          buttons={paymentButtons}
          selected={paymentType}
          gaEventHandlers={{
            load: () => paymentMethodButtonsLoadGAEvent(paymentType),
          }}
        />
      )}

      {hasSpreedlyId && paymentType === paymentTypes.full && (
        <SpreedlyExpressForm
          total={total}
          fullName={transaction?.petData?.owner?.name}
          practiceName={practice?.name}
          invoiceId={transaction?.invoiceId}
          formType={formType}
          spreedlyEnvironmentKey={Env.spreedlyEnvironmentKey}
          spreedlyTokenSuccessHandler={handleSpreedlyTokenSuccess}
          spreedlyCloseHandler={handleClosePaymentForm}
          spreedlyScriptLoadErrorHandler={handleSpreedlyScriptLoadError}
          showForm={showForm}
        />
      )}

      {hasPayFabricId && paymentType === paymentTypes.full && (
        <Modal
          show={showForm}
          onHide={
            showOverwriteSavedCardModal
              ? async () => closeOverwriteModal(true)
              : handleClosePaymentForm
          }
          size='md'
          aria-labelledby='payment-modal'
          centered
        >
          {showOverwriteSavedCardModal && (
            <ModalHeader>
              <ModalTitle>Overwrite Existing Saved Card</ModalTitle>
            </ModalHeader>
          )}

          <ModalBody>
            {isLoading && (
              <div className={styles.formLoading}>
                <Loading />
              </div>
            )}

            {!showOverwriteSavedCardModal && (
              <div className={styles.payFabricHostedForm}>
                {token && (
                  <PayFabricPaymentsSDK
                    token={token}
                    handleFormLoaded={() => setIsLoading(false)}
                    handleCancel={handleCancelPayFabricForm}
                    handleTransactionCompleted={
                      handlePayFabricTransactionCompleted
                    }
                    handleTransactionFailed={handlePayFabricTransactionFailed}
                    isProduction={Env.environment === Environments.Production}
                  />
                )}
              </div>
            )}

            {showOverwriteSavedCardModal && (
              <div className={styles.textContainer}>
                You already have a card saved with us, would you like to
                overwrite your existing saved card?
              </div>
            )}
          </ModalBody>
          {showOverwriteSavedCardModal && (
            <ModalFooter>
              <div className={styles.buttonContainer}>
                <Button
                  variant='light'
                  onClick={async () => closeOverwriteModal(true)}
                  disabled={isLoading}
                >
                  NO
                </Button>
                <Button
                  variant='secondary'
                  onClick={async () => overwriteSavedCard()}
                  disabled={isLoading}
                >
                  YES
                </Button>
              </div>
            </ModalFooter>
          )}
        </Modal>
      )}

      {splititInitUrl && paymentType === paymentTypes.installments && (
        <SplititHostedForm
          showForm={showForm}
          formType={formType}
          transactionId={
            transaction?.transactionId
              ? transaction?.transactionId
              : transaction?.charge?.id
          }
          practiceId={practice?.id}
          practiceName={practice?.name}
          invoiceId={transaction?.invoiceId}
          amount={total}
          splititInitUrl={splititInitUrl}
          redirectUrls={redirectUrls}
          fullName={transaction?.petData?.owner?.name}
          phoneNumber={transaction?.petData?.owner?.phone}
          staffMember={transaction?.charge?.user?.name}
          splititLoadingEndHandler={() => {
            console.log('%csplitit loaded', 'color: limegreen');
          }}
        />
      )}

      {hasRectangleId && paymentType === paymentTypes.full && (
        <Modal
          show={showForm}
          onHide={
            showOverwriteSavedCardModal
              ? async () => closeOverwriteModal(true)
              : handleClosePaymentForm
          }
          size='md'
          aria-labelledby='payment-modal'
          centered
        >
          {showOverwriteSavedCardModal && (
            <ModalHeader>
              <ModalTitle>Overwrite Existing Saved Card</ModalTitle>
            </ModalHeader>
          )}
          {!showOverwriteSavedCardModal && (
            <ModalHeader>
              <ModalTitle>Payment Details</ModalTitle>
            </ModalHeader>
          )}
          <ModalBody>
            {isLoading && (
              <div className={styles.formLoading}>
                <Loading />
              </div>
            )}

            {!showOverwriteSavedCardModal && (
              <div>
                <PaymentRectangle
                  publicKey={practice?.rectanglePublicKey}
                  amount={transaction.charge.amount}
                  backAction={handleClosePaymentForm}
                  completePaymentAction={handleRectangleTokenResponse}
                  payWithSavedCards={practice.payWithSavedCards}
                ></PaymentRectangle>
              </div>
            )}

            {showOverwriteSavedCardModal && (
              <div className={styles.textContainer}>
                You already have a card saved with us, would you like to
                overwrite your existing saved card?
              </div>
            )}
          </ModalBody>
          {showOverwriteSavedCardModal && (
            <ModalFooter>
              <div className={styles.buttonContainer}>
                <Button
                  variant='light'
                  onClick={async () => closeOverwriteModal(true)}
                  disabled={isLoading}
                >
                  NO
                </Button>
                <Button
                  variant='secondary'
                  onClick={async () => overwriteSavedCard()}
                  disabled={isLoading}
                >
                  YES
                </Button>
              </div>
            </ModalFooter>
          )}
        </Modal>
      )}

      {showMainContent && (
        <MakePaymentButton
          clickHandler={async () => handleMakePayment()}
          isDisabled={showForm}
        />
      )}
    </TokenPayProvider>
  );
}

export default PaymentLinkMainContent;
